import React from 'react';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { HeroSection } from '../client/types';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { COMING_SOON_AGENCY_LISTING } from '../client/graphql/queries/comingSoonAgencyListing';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import Hero from '../client/templates/common/hero/Hero';
import PageSection from '../client/components/page-section/PageSection';
import {
  buildAgencyByLocalityURLObject,
  relativeToAbsoluteURL,
} from '../client/utils/links';
import { ComingSoonAgencyListing } from '../domain/graphcms';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import {
  AgenciesSection, AgenciesSectionProps, GenericSection, GenericSectionProps, StripesSection, StripesSectionProps,
} from '../client/sections';

export interface ComingSoonAgenciesProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection, // @TODO: use correct prop types
  introSection: GenericSectionProps,
  comingSoonAgenciesSection: AgenciesSectionProps,
  joinUsSection: StripesSectionProps,
}

export const ComingSoonAgencies: React.FC<ComingSoonAgenciesProps> = ({
  SEO,
  hero,
  introSection,
  comingSoonAgenciesSection,
  joinUsSection,
}) => (
  <AppLayout>
    <PageHead SEO={SEO} />
    <PageLayout>
      <Hero
        title={hero.title}
        description={hero.description}
        backgroundImage={hero.backgroundImage}
      />
      <PageLayout.Content>
        <PageLayout.Main>
          <GenericSection {...introSection} />

          <AgenciesSection {...comingSoonAgenciesSection} />

          {joinUsSection && (
            <StripesSection {...joinUsSection} />
          )}
        </PageLayout.Main>
      </PageLayout.Content>
      <PageSection.Wrapper>
        <PageLayout.Breadcrumb items={[{ label: 'Prossime agenzie immobiliari' }]} />
      </PageSection.Wrapper>
    </PageLayout>
  </AppLayout>
);

export const getServerSideProps = withGetServerSideError<ComingSoonAgenciesProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const comingSoonAgencyListings = await graphQLClient.query<{
    comingSoonAgencyListings: ComingSoonAgencyListing[],
  }>({ query: COMING_SOON_AGENCY_LISTING });

  const {
    seo, hero, introSection, comingSoonAgenciesSection, joinUsSection,
  } = comingSoonAgencyListings.data.comingSoonAgencyListings[0];
  const comingSoonAgenciesListingURLObject = buildAgencyByLocalityURLObject({ locality: 'altrecitta' });

  return {
    props: {
      SEO: {
        ...seo,
        canonical: relativeToAbsoluteURL(comingSoonAgenciesListingURLObject.as),
      },
      hero: {
        title: hero?.title?.markdown?.trim() || null,
        description: hero?.content?.markdown?.trim() || null,
        backgroundImage: {
          SMALL: hero?.mobileImage?.url || null,
          LARGE: hero?.desktopImage?.url || null,
        },
        imageAlt: hero.imageAlt,
      },
      introSection: {
        title: introSection.title?.markdown || null,
        content: introSection.content?.markdown,
      },
      comingSoonAgenciesSection: {
        title: comingSoonAgenciesSection?.title?.markdown || null,
        content: comingSoonAgenciesSection?.content?.markdown || null,
        agencies: comingSoonAgenciesSection.agencies
          .sort((a, b) => a.locality.name.localeCompare(b.locality.name))
          .map(({
            locality,
          }) => ({
            name: locality.name,
            picture: locality.coverPreviewHorizontal?.url || requireFromStaticAsset('cities/small/default-city.png'),
            title: `Agenzia immobiliare ${locality.name}`,
            urlObject: buildAgencyByLocalityURLObject({ locality: locality.slug }),
            overriddenAlt: `Agenzia immobiliare a ${locality.name}`,
          })),
      },
      joinUsSection: joinUsSection.items.length > 0 ? {
        title: joinUsSection.title?.markdown || null,
        content: joinUsSection.content?.markdown || null,
        items: joinUsSection.items.map((item) => ({
          title: item.title,
          subtitle: item.subtitle,
          content: item.content,
          image: {
            url: item.image.url,
            alt: item.imageAlt,
          },
          action: item.action,
          isAbsoluteImageUrl: true,
        })),
      } : null,
    },
  };
});
export default withError(ComingSoonAgencies);
